import { useRef, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Box, Button, Checkbox, Collapse, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Stack, Tab, Tabs, TextField } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment'
import useFetch from './api/api'
import NumericField from './components/NumericField'
import config from './common/Config';

const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']

const emptyErrors = () => ({
	name:      '',
	symbol:    '',
	coupon:    '',
	maturity:  '',
	payMonths: '',
})

function Bonds() {
	const [currentTabIndex, setCurrentTabIndex] = useState(0);
	const [alertOpen, setAlertOpen]     = useState(false)
	const [alertSeverity, setAlertSeverity] = useState()
	const [alertMessage, setAlertMessage]   = useState();

	const { country } = useParams();

	const [name, setName]         = useState(country === 'US' ? 'UNITED STATES TREAS NTS' : '')
	const [symbol, setSymbol]     = useState('')
	const [coupon, setCoupon]     = useState('')
	const [maturity, setMaturity] = useState(null)
	const [payMonths, setPayMonths] = useState([]);

	const [errors, setErrors]           = useState(emptyErrors())
	const hasTriedSubmit                = useRef(false)

	const [bonds, setBonds] = useState();

	const privateFetch = useFetch();

	const columns = [
		{ field: 'name',      headerName: 'Nome',               disableColumnMenu: true, width: 300 },
		{ field: 'symbol',    headerName: 'Symbol',             disableColumnMenu: true, width: 150 },
		{ field: 'yield',     headerName: 'Cupom',              disableColumnMenu: true },
		{
			field: 'maturity',
			headerName: 'Vencimento',
			disableColumnMenu: true,
			valueFormatter: (v) => v ? moment(v).format('DD/MM/YYYY') : '—'
		},
		{ field: 'payMonths', headerName: 'Pagamentos',         disableColumnMenu: true, width: 250 },
		{
			field: 'price',
			headerName: 'Cotação',
			disableColumnMenu: true,
			align: 'center',
			headerAlign: 'center',
			valueFormatter: (v) => v
				? parseFloat(v).toLocaleString(country === 'BR' ? 'pt-BR' : 'en-US', {
					style: 'currency',
					currency: country === 'BR' ? 'BRL' : 'USD',
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})
				: '—'
		},
		{
			field: 'priceDate',
			headerName: 'Atualização',
			disableColumnMenu: true,
			align: 'center',
			headerAlign: 'center',
			valueFormatter: (v) => v ? moment(v).format('DD/MM/YYYY') : '—'
		},
	]

	useEffect(() => {
		privateFetch(config.apiUrl + '/api/bonds/' + country)
			.then(data => data.json())
			.then(data => setBonds(data))
	}, [country])

	function addProps(index) {
		return {
			id: `simple-tab-${index}`,
			'aria-controls': `simple-tabpanel-${index}`,
		};
	}

	const handleTabChange = (e, tabIndex) => {
		setCurrentTabIndex(tabIndex);
	};

	const checkValidity = () => {
		let isValid = true
		const next  = emptyErrors()

		if (!name || name.trim().length === 0) {
			next.name = 'Informe o nome do título'
			isValid = false
		}
		if (!symbol || symbol.trim().length === 0) {
			next.symbol = 'Informe o símbolo'
			isValid = false
		}
		if (!coupon || isNaN(parseFloat(coupon))) {
			next.coupon = 'Informe o cupom (ex: 4,25)'
			isValid = false
		}
		if (!maturity) {
			next.maturity = 'Informe a data de vencimento'
			isValid = false
		}
		if (payMonths.length === 0) {
			next.payMonths = 'Selecione ao menos um mês de pagamento'
			isValid = false
		}

		setErrors(next)
		return isValid
	}

	useEffect(() => {
		if (hasTriedSubmit.current) checkValidity()
	}, [name, symbol, coupon, maturity, payMonths])

	const handleSave = () => {
		hasTriedSubmit.current = true
		if (!checkValidity()) return

		const options = {
			method: 'POST',
			headers: { 'content-type': 'application/json; charset=utf-8' },
			body: JSON.stringify({ name, symbol, country: 'US', coupon, maturity, payMonths })
		}

		privateFetch(config.apiUrl + '/api/bonds', options)
			.then(ret => {
				switch (ret.status) {
					case 200:
						setAlertMessage('Titulo inserido com sucesso')
						setAlertSeverity('success')
						setAlertOpen(true)
						break
					case 500:
						setAlertMessage('Erro ao inserir titulo')
						setAlertSeverity('error')
						setAlertOpen(true)
						break
				}
			})
	}

	return (
		<Paper sx={{ marginLeft: 2, marginRight: 4 }}>
			<Collapse in={alertOpen}>
				<Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>{alertMessage}</Alert>
			</Collapse>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs value={currentTabIndex} onChange={handleTabChange} aria-label="basic tabs example">
					<Tab label="Títulos" {...addProps(0)} />
					<Tab label="Adicionar Novo" {...addProps(1)} />
				</Tabs>
			</Box>

			{currentTabIndex === 0 && bonds !== undefined && (
				<DataGrid
					rows={bonds}
					getRowId={(row) => row.id}
					columns={columns}
					pageSize={12}
					rowHeight={30}
					autoHeight={true}
				/>
			)}

			{currentTabIndex === 1 && (
				<Stack spacing={2} padding={2} direction="row" alignItems="flex-start" flexWrap="wrap">
					<TextField
						label="Titulo"
						sx={{ width: 200 }}
						value={name}
						error={!!errors.name}
						helperText={errors.name}
						onChange={e => setName(e.target.value)}
					/>
					<TextField
						label="Symbol"
						sx={{ width: 200 }}
						value={symbol}
						error={!!errors.symbol}
						helperText={errors.symbol}
						onChange={e => setSymbol(e.target.value)}
					/>
					<NumericField
						label="Cupom"
						adornment="%"
						sx={{ width: 200 }}
						value={coupon}
						error={!!errors.coupon}
						helperText={errors.coupon}
						onChange={e => setCoupon(e.target.value)}
					/>
					<DatePicker
						format="DD/MM/YYYY"
						label="Vencimento"
						slotProps={{
							textField: {
								sx: { width: 200 },
								error: !!errors.maturity,
								helperText: errors.maturity,
							}
						}}
						onChange={value => setMaturity(value ? new Date(value).toISOString().substring(0, 10) : null)}
					/>
					<FormControl sx={{ width: 250 }} error={!!errors.payMonths}>
						<InputLabel id="payMonthsLabel">Pagamentos</InputLabel>
						<Select
							labelId="payMonthsLabel"
							multiple
							input={<OutlinedInput label="Pagamentos" />}
							value={payMonths}
							renderValue={selected => selected.join(', ')}
							onChange={e => setPayMonths(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
						>
							{MONTHS.map(month => (
								<MenuItem key={month} value={month}>
									<Checkbox checked={payMonths.indexOf(month) > -1} />
									<ListItemText primary={month} />
								</MenuItem>
							))}
						</Select>
						{errors.payMonths && <FormHelperText>{errors.payMonths}</FormHelperText>}
					</FormControl>
					<Button variant="contained" onClick={handleSave} sx={{ alignSelf: 'center' }}>Salvar</Button>
				</Stack>
			)}
		</Paper>
	);
}

export default Bonds
